import { existsSync } from 'node:fs';
import { mkdir, readdir, readFile, writeFile } from 'node:fs/promises';
import { dirname, join } from 'node:path';

import { PATHS } from '@/core/constants/paths.js';
import type {
  PentestRunProgress,
  PentestRunStatus,
  PentestRunStep,
  PentestWorkflowName,
} from '@/core/types/pentest.js';
import { VERSION } from '@/index.js';
import { SchemaValidator } from '@/validators/validator.js';

import { toLocalTimestamp } from './shared.js';

export interface EnsurePentestRunOptions {
  projectRoot: string;
  workflow: PentestWorkflowName;
  sourceReportPath?: string | null;
  targetUrl?: string | null;
  steps: Array<Pick<PentestRunStep, 'id' | 'title'>>;
}

export class PentestProgressTracker {
  constructor(private readonly validator = new SchemaValidator()) {}

  async ensureRun(options: EnsurePentestRunOptions): Promise<PentestRunProgress> {
    const existing = await this.findIncompleteRun(
      options.projectRoot,
      options.workflow,
      options.sourceReportPath ?? null,
    );

    if (existing !== null) {
      const reset = this.resetRunningSteps(existing);
      if (reset) {
        await this.save(options.projectRoot, existing);
      }
      return existing;
    }

    const now = new Date().toISOString();
    return {
      schema_version: '1',
      generated_by: 'paqad-ai',
      framework_version: VERSION,
      run_id: toLocalTimestamp(new Date()),
      workflow: options.workflow,
      status: 'running',
      started_at: now,
      updated_at: now,
      report_path: null,
      sidecar_path: null,
      target_url: options.targetUrl ?? null,
      source_report_path: options.sourceReportPath ?? null,
      steps: options.steps.map((step) => ({
        ...step,
        status: 'not_started',
        input_hash: null,
        artifact_paths: [],
        skill_names: [],
        error: null,
        started_at: null,
        completed_at: null,
      })),
      skill_invocations: [],
      script_artifacts: [],
      current_finding_ids: [],
    };
  }

  async load(projectRoot: string, runId: string): Promise<PentestRunProgress | null> {
    const target = join(projectRoot, PATHS.PENTEST_RUNS_DIR, runId, 'progress.json');
    if (!existsSync(target)) {
      return null;
    }

    const parsed = JSON.parse(await readFile(target, 'utf8')) as PentestRunProgress;
    const validation = this.validator.validate('pentest-progress', parsed);
    if (!validation.valid) {
      throw new Error(validation.errors.map((error) => error.message).join('; '));
    }
    return parsed;
  }

  async save(projectRoot: string, progress: PentestRunProgress): Promise<void> {
    const target = join(projectRoot, PATHS.PENTEST_RUNS_DIR, progress.run_id, 'progress.json');
    progress.updated_at = new Date().toISOString();
    await mkdir(dirname(target), { recursive: true });
    await writeFile(target, `${JSON.stringify(progress, null, 2)}\n`);
  }

  markStepRunning(progress: PentestRunProgress, stepId: string, inputHash: string): void {
    const step = this.findStep(progress, stepId);
    step.status = 'running';
    step.input_hash = inputHash;
    step.error = null;
    step.started_at = new Date().toISOString();
    step.completed_at = null;
    progress.status = 'running';
  }

  markStepCompleted(
    progress: PentestRunProgress,
    stepId: string,
    artifactPaths: string[],
    skillNames: string[],
  ): void {
    const step = this.findStep(progress, stepId);
    step.status = 'completed';
    step.artifact_paths = [...new Set([...step.artifact_paths, ...artifactPaths])];
    step.skill_names = [...new Set([...step.skill_names, ...skillNames])];
    step.error = null;
    step.completed_at = new Date().toISOString();
    progress.skill_invocations = [...new Set([...progress.skill_invocations, ...skillNames])];
    progress.script_artifacts = [...new Set([...progress.script_artifacts, ...artifactPaths])];
  }

  markStepBlocked(
    progress: PentestRunProgress,
    stepId: string,
    error: string,
    artifactPaths: string[],
    skillNames: string[],
  ): void {
    const step = this.findStep(progress, stepId);
    step.status = 'blocked';
    step.error = error;
    step.artifact_paths = [...new Set([...step.artifact_paths, ...artifactPaths])];
    step.skill_names = [...new Set([...step.skill_names, ...skillNames])];
    step.completed_at = new Date().toISOString();
    progress.status = 'blocked';
    progress.skill_invocations = [...new Set([...progress.skill_invocations, ...skillNames])];
    progress.script_artifacts = [...new Set([...progress.script_artifacts, ...artifactPaths])];
  }

  markStepFailed(
    progress: PentestRunProgress,
    stepId: string,
    error: string,
    artifactPaths: string[],
    skillNames: string[],
  ): void {
    const step = this.findStep(progress, stepId);
    step.status = 'failed';
    step.error = error;
    step.artifact_paths = [...new Set([...step.artifact_paths, ...artifactPaths])];
    step.skill_names = [...new Set([...step.skill_names, ...skillNames])];
    step.completed_at = new Date().toISOString();
    progress.status = 'failed';
    progress.skill_invocations = [...new Set([...progress.skill_invocations, ...skillNames])];
    progress.script_artifacts = [...new Set([...progress.script_artifacts, ...artifactPaths])];
  }

  markRunCompleted(
    progress: PentestRunProgress,
    reportPath: string,
    sidecarPath: string,
    findingIds: string[],
  ): void {
    progress.status = 'completed';
    progress.report_path = reportPath;
    progress.sidecar_path = sidecarPath;
    progress.current_finding_ids = [...findingIds];
    progress.updated_at = new Date().toISOString();
  }

  shouldSkipStep(progress: PentestRunProgress, stepId: string, inputHash: string): boolean {
    const step = this.findStep(progress, stepId);
    return step.status === 'completed' && step.input_hash === inputHash;
  }

  private async findIncompleteRun(
    projectRoot: string,
    workflow: PentestWorkflowName,
    sourceReportPath: string | null,
  ): Promise<PentestRunProgress | null> {
    const runsDir = join(projectRoot, PATHS.PENTEST_RUNS_DIR);
    if (!existsSync(runsDir)) {
      return null;
    }

    const entries = (await readdir(runsDir, { withFileTypes: true }))
      .filter((entry) => entry.isDirectory())
      .map((entry) => entry.name)
      .sort()
      .reverse();

    for (const runId of entries) {
      const progress = await this.load(projectRoot, runId);
      if (progress === null) {
        continue;
      }

      const sameSource =
        progress.source_report_path === sourceReportPath ||
        (progress.source_report_path === null && sourceReportPath === null);
      if (progress.workflow === workflow && progress.status !== 'completed' && sameSource) {
        return progress;
      }
    }

    return null;
  }

  private resetRunningSteps(progress: PentestRunProgress): boolean {
    let changed = false;

    for (const step of progress.steps) {
      if (step.status !== 'running') {
        continue;
      }

      step.status = 'not_started';
      step.started_at = null;
      step.completed_at = null;
      step.error = null;
      changed = true;
    }

    if (changed && progress.status === 'running') {
      progress.status = 'blocked';
    }

    return changed;
  }

  private findStep(progress: PentestRunProgress, stepId: string): PentestRunStep {
    const step = progress.steps.find((candidate) => candidate.id === stepId);
    if (!step) {
      throw new Error(`Unknown pentest step: ${stepId}`);
    }
    return step;
  }
}

export function runArtifactsDir(projectRoot: string, runId: string): string {
  return join(projectRoot, PATHS.PENTEST_RUNS_DIR, runId, 'artifacts');
}

export function runLogsDir(projectRoot: string, runId: string): string {
  return join(projectRoot, PATHS.PENTEST_RUNS_DIR, runId, 'logs');
}

export function runStatusForSteps(steps: PentestRunStep[]): PentestRunStatus {
  if (steps.some((step) => step.status === 'failed')) {
    return 'failed';
  }

  if (steps.some((step) => step.status === 'blocked')) {
    return 'blocked';
  }

  if (steps.every((step) => step.status === 'completed')) {
    return 'completed';
  }

  return 'running';
}
